<script setup>
import { inject, computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Components
import MetricCard from '../components/MetricCard.vue';
import EvolutionChart from '../components/EvolutionChart.vue';
import StatusDonutChart from '../components/StatusDonutChart.vue';
import WeeklyTrendChart from '../components/WeeklyTrendChart.vue';
import HourlyBarChart from '../components/HourlyBarChart.vue';
import DayOfWeekChart from '../components/DayOfWeekChart.vue';
import ResponseTimeDistribution from '../components/ResponseTimeDistribution.vue';
import ResponseTimeMetricsCard from '../components/ResponseTimeMetricsCard.vue';
import MessagesAnalysisCard from '../components/MessagesAnalysisCard.vue';
import PeakHoursCard from '../components/PeakHoursCard.vue';
import ContactStatsCard from '../components/ContactStatsCard.vue';

const { t } = useI18n();

const {
  summaryData,
  previousSummaryData,
  conversationData,
  conversationMetrics,
  conversationsDetailed,
  isLoading,
} = inject('analyticsData');

// Use detailed data from parent (already fetched centrally with 5s refresh)
const detailedData = computed(() => conversationsDetailed.value);

// Format large numbers
function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

// Format time
function formatMinutes(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)} h`;
}

function formatHours(hours) {
  if (!hours || hours === 0) return '0 h';
  if (hours < 24) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} d`;
}

// Calculate percentage change
function calculateChange(current, previous) {
  if (!previous || previous === 0) return current > 0 ? 100 : 0;
  return (((current - previous) / previous) * 100).toFixed(1);
}

// Conversation status data for donut
const statusData = computed(() => {
  const breakdown = detailedData.value?.status_breakdown;
  if (breakdown) {
    return breakdown.map(s => ({
      label: s.label,
      value: s.count,
      color: s.color,
    }));
  }

  // Fallback to basic data
  if (!summaryData.value) return [];
  const total = summaryData.value.conversations_count || 0;
  const resolved = summaryData.value.resolutions_count || 0;
  const open = conversationMetrics.value.open || 0;
  const pending = conversationMetrics.value.pending || 0;
  const snoozed = total - resolved - open - pending;

  return [
    { label: 'Abertas', value: open, color: '#3b82f6' },
    { label: 'Resolvidas', value: resolved, color: '#22c55e' },
    { label: 'Pendentes', value: pending, color: '#f59e0b' },
    { label: 'Adiadas', value: Math.max(0, snoozed), color: '#6b7280' },
  ];
});

// Summary computed values
const summary = computed(() => detailedData.value?.summary || {});
const responseTimesData = computed(
  () => detailedData.value?.response_times || {}
);
const hourlyDistribution = computed(
  () => detailedData.value?.hourly_distribution || []
);
const dailyDistribution = computed(
  () => detailedData.value?.daily_distribution || []
);
const weeklyTrend = computed(() => detailedData.value?.weekly_trend || []);
const messagesAnalysis = computed(
  () => detailedData.value?.messages_analysis || {}
);
const firstResponseDistribution = computed(
  () => detailedData.value?.first_response_distribution || []
);
const resolutionDistribution = computed(
  () => detailedData.value?.resolution_time_distribution || []
);
const peakHours = computed(() => detailedData.value?.peak_hours || {});
const contactStats = computed(() => detailedData.value?.contact_stats || {});
const priorityBreakdown = computed(
  () => detailedData.value?.priority_breakdown || []
);
const waitTimeAnalysis = computed(
  () => detailedData.value?.wait_time_analysis || {}
);

// Computed metrics with change indicators
const conversationsChange = computed(() => {
  if (!summaryData.value || !previousSummaryData.value) return null;
  return calculateChange(
    summaryData.value.conversations_count,
    previousSummaryData.value.conversations_count
  );
});

const resolutionsChange = computed(() => {
  if (!summaryData.value || !previousSummaryData.value) return null;
  return calculateChange(
    summaryData.value.resolutions_count,
    previousSummaryData.value.resolutions_count
  );
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Main Metrics Cards -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <MetricCard
        icon="i-lucide-message-square"
        :title="$t('ANALYTICS_DASHBOARD.CONVERSATIONS.TOTAL')"
        :value="
          formatNumber(
            summary.total_conversations || summaryData?.conversations_count || 0
          )
        "
        :change="conversationsChange"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-check-circle"
        :title="$t('ANALYTICS_DASHBOARD.CONVERSATIONS.RESOLVED')"
        :value="
          formatNumber(
            summary.resolved_conversations ||
              summaryData?.resolutions_count ||
              0
          )
        "
        :change="resolutionsChange"
        color="green"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-inbox"
        :title="$t('ANALYTICS_DASHBOARD.CONVERSATIONS.OPEN')"
        :value="formatNumber(conversationMetrics.open)"
        color="blue"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-clock"
        :title="$t('ANALYTICS_DASHBOARD.CONVERSATIONS.PENDING')"
        :value="formatNumber(conversationMetrics.pending)"
        color="amber"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-percent"
        title="Taxa Resolucao"
        :value="`${summary.resolution_rate || 0}%`"
        color="teal"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-reply"
        title="Taxa Resposta"
        :value="`${summary.reply_rate || 0}%`"
        color="violet"
        :loading="isLoading"
      />
    </div>

    <!-- Response Time Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <MetricCard
        icon="i-lucide-timer"
        title="1a Resposta (Media)"
        :value="formatMinutes(summary.avg_first_response_time_minutes)"
        :subtitle="`Mediana: ${formatMinutes(summary.median_first_response_time_minutes)}`"
        color="blue"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-timer"
        title="Resolucao (Media)"
        :value="formatHours(summary.avg_resolution_time_hours)"
        :subtitle="`Mediana: ${formatHours(summary.median_resolution_time_hours)}`"
        color="teal"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-message-circle"
        title="Total Mensagens"
        :value="formatNumber(summary.total_messages || messagesAnalysis.total)"
        :subtitle="`${formatNumber(summary.avg_messages_per_conversation || 0)} por conversa`"
        color="purple"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-alert-circle"
        title="Aguardando"
        :value="formatNumber(waitTimeAnalysis.conversations_waiting || 0)"
        :subtitle="`${waitTimeAnalysis.waiting_over_1_hour || 0} ha mais de 1h`"
        color="red"
        :loading="isLoading"
      />
    </div>

    <!-- Weekly Trend Chart (Full Width) -->
    <WeeklyTrendChart
      :data="weeklyTrend"
      :loading="isLoading"
      :title="$t('ANALYTICS_DASHBOARD.CONVERSATIONS.DAILY_EVOLUTION')"
    />

    <!-- Charts Row 1: Evolution and Status -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div class="lg:col-span-2">
        <EvolutionChart
          :data="conversationData"
          :loading="isLoading"
          title="Conversas por Dia"
        />
      </div>
      <StatusDonutChart
        :data="statusData"
        :loading="isLoading"
        :title="$t('ANALYTICS_DASHBOARD.CONVERSATIONS.BY_STATUS')"
      />
    </div>

    <!-- Charts Row 2: Time Distribution -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <HourlyBarChart
        :data="hourlyDistribution"
        :loading="isLoading"
        title="Distribuicao por Hora do Dia"
      />
      <DayOfWeekChart
        :data="dailyDistribution"
        :loading="isLoading"
        title="Distribuicao por Dia da Semana"
      />
    </div>

    <!-- Charts Row 3: Response Times -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ResponseTimeMetricsCard
        :data="responseTimesData"
        :loading="isLoading"
        title="Metricas de Tempo de Resposta"
      />
      <div class="flex flex-col gap-4">
        <ResponseTimeDistribution
          :data="firstResponseDistribution"
          :loading="isLoading"
          title="Distribuicao - 1a Resposta"
        />
        <ResponseTimeDistribution
          :data="resolutionDistribution"
          :loading="isLoading"
          title="Distribuicao - Resolucao"
        />
      </div>
    </div>

    <!-- Charts Row 4: Messages, Peak Hours, Contacts -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <MessagesAnalysisCard
        :data="messagesAnalysis"
        :loading="isLoading"
        title="Analise de Mensagens"
      />
      <PeakHoursCard
        :data="peakHours"
        :loading="isLoading"
        title="Horarios de Pico"
      />
      <ContactStatsCard
        :data="contactStats"
        :loading="isLoading"
        title="Estatisticas de Contatos"
      />
    </div>

    <!-- Priority Breakdown -->
    <div
      v-if="priorityBreakdown.length > 0"
      class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
    >
      <div class="flex items-center gap-2 mb-4">
        <span class="i-lucide-flag text-lg text-n-amber-10" />
        <h3 class="text-base font-semibold text-n-slate-12">
          Distribuicao por Prioridade
        </h3>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div
          v-for="priority in priorityBreakdown"
          :key="priority.priority"
          class="text-center p-3 rounded-lg"
          :style="{ backgroundColor: `${priority.color}20` }"
        >
          <div class="text-2xl font-bold" :style="{ color: priority.color }">
            {{ priority.count }}
          </div>
          <div class="text-xs text-n-slate-11">{{ priority.label }}</div>
          <div class="text-xs text-n-slate-10">
            ({{ priority.percentage }}%)
          </div>
        </div>
      </div>
    </div>

    <!-- Wait Time Alert Section -->
    <div
      v-if="waitTimeAnalysis.conversations_waiting > 0"
      class="rounded-xl border border-n-ruby-5 bg-n-ruby-3/30 p-4"
    >
      <div class="flex items-center gap-3">
        <span class="i-lucide-alert-triangle text-2xl text-n-ruby-10" />
        <div class="flex-1">
          <h3 class="text-base font-semibold text-n-ruby-11">
            Conversas Aguardando Resposta
          </h3>
          <p class="text-sm text-n-slate-11 mt-1">
            {{ waitTimeAnalysis.conversations_waiting }} conversas aguardando
            resposta.
            <span
              v-if="waitTimeAnalysis.waiting_over_4_hours > 0"
              class="font-medium text-n-ruby-11"
            >
              {{ waitTimeAnalysis.waiting_over_4_hours }} ha mais de 4 horas!
            </span>
          </p>
        </div>
        <div class="text-right">
          <div class="text-2xl font-bold text-n-ruby-11">
            {{ formatMinutes(waitTimeAnalysis.avg_wait_time_minutes) }}
          </div>
          <div class="text-xs text-n-slate-11">tempo medio de espera</div>
        </div>
      </div>
    </div>
  </div>
</template>
